import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Filter, Eye } from "lucide-react";
import { eur, formatDate } from "@/lib/format";

export function AgentsTab() {
  const [agents, setAgents] = useState(null);
  const [managers, setManagers] = useState([]);
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [salesTarget, setSalesTarget] = useState(null);
  const [salesData, setSalesData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => setAgents([]));
    api.get("/admin/managers").then((r) => setManagers(r.data)).catch(() => setManagers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createAgent = async () => {
    try {
      await api.post("/admin/agents", form);
      toast.success("Agente creato");
      setCreateOpen(false); setForm({ full_name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignManager = async (agentId, managerId) => {
    try {
      await api.patch(`/admin/agents/${agentId}/manager`, { manager_id: managerId === "none" ? null : managerId });
      toast.success("Capo area aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (agent) => {
    try {
      await api.patch(`/admin/agents/${agent.id}`, { is_active: !agent.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPassword = async () => {
    try {
      await api.patch(`/admin/agents/${pwdTarget.id}`, { password: newPwd });
      toast.success("Password reimpostata");
      setPwdTarget(null); setNewPwd("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeAgent = async (id) => {
    try { await api.delete(`/admin/agents/${id}`); toast.success("Agente eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewSales = async (agent) => {
    setSalesTarget(agent); setSalesData(null);
    try {
      const r = await api.get(`/admin/agents/${agent.id}/sales`);
      setSalesData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSalesTarget(null); }
  };

  if (!agents) return <Skeleton className="h-64 bg-[#FFFFFF]" />;
  const rows = onlyUnassigned ? agents.filter((a) => !a.manager_id) : agents;

  return (
    <div className="space-y-5" data-testid="agents-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => setOnlyUnassigned((v) => !v)}
          className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md border transition-colors ${onlyUnassigned ? "border-[#0B6BCB] text-[#0B6BCB] bg-[#0B6BCB]/10" : "border-[#DCE7F5] text-[#5A6B85] hover:text-[#0F1E33]"}`}
          data-testid="filter-unassigned-btn"
        >
          <Filter className="w-4 h-4" strokeWidth={1.5} /> Solo non assegnati
        </button>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="create-agent-btn">
          <UserPlus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Nuovo agente
        </Button>
      </div>

      <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                <TableHead className="text-[#5A6B85]">Agente</TableHead>
                <TableHead className="text-[#5A6B85]">Capo Area</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Vendite</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Premi</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Valore €</TableHead>
                <TableHead className="text-[#5A6B85] text-center">Attivo</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} className="border-[#DCE7F5]" data-testid={`agent-row-${a.id}`}>
                  <TableCell>
                    <button onClick={() => viewSales(a)} className="text-left group" data-testid={`view-agent-sales-${a.id}`}>
                      <div className="font-medium text-[#0F1E33] group-hover:text-[#0B6BCB] transition-colors underline decoration-dotted decoration-[#B9CCE8] underline-offset-2">{a.full_name}</div>
                      <div className="text-xs text-[#5A6B85]">{a.email}</div>
                    </button>
                  </TableCell>
                  <TableCell>
                    <Select value={a.manager_id || "none"} onValueChange={(v) => assignManager(a.id, v)}>
                      <SelectTrigger className="w-40 bg-[#F5F9FF] border-[#DCE7F5] h-9" data-testid={`assign-manager-${a.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]">
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right text-[#5A6B85]">{a.count}</TableCell>
                  <TableCell className="text-right text-[#5A6B85]">{a.prizes_won}</TableCell>
                  <TableCell className="text-right font-semibold text-[#0B6BCB]">{eur(a.prize_value)}</TableCell>
                  <TableCell className="text-center">
                    <Switch checked={a.is_active} onCheckedChange={() => toggleActive(a)} data-testid={`toggle-active-${a.id}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-[#0B6BCB]" onClick={() => viewSales(a)} data-testid={`view-sales-btn-${a.id}`}>
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-[#0B6BCB]" onClick={() => setPwdTarget(a)} data-testid={`reset-pwd-${a.id}`}>
                      <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-red-600" onClick={() => removeAgent(a.id)} data-testid={`delete-agent-${a.id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#5A6B85] py-8">Nessun agente</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="create-agent-dialog">
          <DialogHeader><DialogTitle className="font-display">Nuovo agente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome completo" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="agent-name-input" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="agent-email-input" />
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="agent-password-input" />
          </div>
          <DialogFooter>
            <Button onClick={createAgent} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-agent-btn">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwdTarget} onOpenChange={(o) => !o && setPwdTarget(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="reset-pwd-dialog">
          <DialogHeader><DialogTitle className="font-display">Reset password · {pwdTarget?.full_name}</DialogTitle></DialogHeader>
          <Field label="Nuova password" type="password" value={newPwd} onChange={setNewPwd} testid="new-password-input" />
          <DialogFooter>
            <Button onClick={resetPassword} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-password-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!salesTarget} onOpenChange={(o) => { if (!o) { setSalesTarget(null); setSalesData(null); } }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33] max-w-2xl" data-testid="agent-sales-dialog">
          <DialogHeader><DialogTitle className="font-display">Vendite di {salesTarget?.full_name}</DialogTitle></DialogHeader>
          {!salesData ? (
            <Skeleton className="h-40 bg-[#F5F9FF]" />
          ) : salesData.breakdown.length === 0 ? (
            <p className="text-sm text-[#5A6B85] py-6 text-center">Nessuna promozione nella campagna attiva.</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-auto pr-1" data-testid="agent-sales-breakdown">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5A6B85]">Totale premio maturato</span>
                <span className="font-display font-bold text-[#0B6BCB]" data-testid="agent-sales-total">{eur(salesData.total_prize_value)}</span>
              </div>
              {salesData.breakdown.map((b) => (
                <div key={b.promotion_id} className="border border-[#DCE7F5] rounded-lg overflow-hidden" data-testid={`agent-sales-promo-${b.promotion_id}`}>
                  <div className="p-3 bg-[#F5F9FF] flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{b.promotion_name}</span>
                      <Badge className="bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5] text-[10px] uppercase">{b.type === "pezzi" ? "Pezzi" : "Vendite"}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm shrink-0">
                      <span className="text-[#5A6B85]">{b.count} / {b.target}</span>
                      <span className={b.achieved ? "font-semibold text-[#0B6BCB]" : "text-[#94A3B8]"}>{eur(b.value)}</span>
                    </div>
                  </div>
                  {b.sales.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-[#94A3B8]">Nessuna vendita registrata</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                          <TableHead className="text-[#5A6B85]">Cliente</TableHead>
                          {b.type === "pezzi" && <TableHead className="text-[#5A6B85] text-right">Pezzi</TableHead>}
                          <TableHead className="text-[#5A6B85]">Note</TableHead>
                          <TableHead className="text-[#5A6B85] text-right">Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {b.sales.map((s) => (
                          <TableRow key={s.id} className="border-[#DCE7F5]" data-testid={`agent-sale-${s.id}`}>
                            <TableCell className="font-medium">{s.client_name || "-"}</TableCell>
                            {b.type === "pezzi" && <TableCell className="text-right text-[#0B6BCB] font-semibold">{s.quantity || 1}</TableCell>}
                            <TableCell className="text-[#5A6B85] text-sm">{s.note || "-"}</TableCell>
                            <TableCell className="text-right text-[#5A6B85] text-sm">{formatDate(s.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#33435E]">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[#F5F9FF] border-[#DCE7F5]" data-testid={testid} />
    </div>
  );
}
